import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Users, Award, FileText, Download, ChevronDown, ChevronUp, Mail, MoreVertical,
  GraduationCap, Star, FolderDown, Info, Check, Search, Grid3X3, List
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SuiviApprenants = ({ sessionId, apprenants = [], sessionTitle = '' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [expandedLearner, setExpandedLearner] = useState(null);
  const [archiveExpanded, setArchiveExpanded] = useState(false);

  const fetchData = useCallback(async () => {
    if (!sessionId) return;
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/suivi-apprenants`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [sessionId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGenerateCertificates = async () => {
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/generate-certificates`, {
        method: 'POST', credentials: 'include',
      });
      if (res.ok) toast.success('Certificats générés avec succès');
    } catch (e) { toast.error('Erreur lors de la génération'); }
  };

  const learners = data?.apprenants || [];

  return (
    <div className="space-y-6 mt-4" data-testid="suivi-apprenants-content">
      {/* Attestations section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-bold text-slate-900 dark:text-white">Attestations</h3>
          <div className="w-5 h-5 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
            <Info className="h-3 w-3 text-slate-400" />
          </div>
        </div>

        {/* Info banner */}
        <div className="border border-violet-200 dark:border-violet-800 rounded-xl p-4 flex items-start gap-3 bg-violet-50/50 dark:bg-violet-900/10" data-testid="attestations-info">
          <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center shrink-0 mt-0.5">
            <Info className="h-3.5 w-3.5 text-white" />
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Les apprenants en situation d'abandon ou d'absence sans date de reprise sont automatiquement désélectionnés de l'envoi des certificats de réalisation, des attestations d'assiduité et des micro-certifications, pensez à les rajouter si vous le souhaitez.
          </p>
        </div>

        {/* Certificate cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="certificates-cards">
          {/* Certificats de réalisation */}
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5">
            <p className="text-xs text-slate-400 italic mb-3">Jamais généré</p>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                <Award className="h-5 w-5 text-violet-500" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-900 dark:text-white">Certificats de réalisation</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" className="text-xs gap-1.5 flex-1" onClick={handleGenerateCertificates}
                data-testid="btn-zip-certificates">
                <FolderDown className="h-3.5 w-3.5" />Zip des certificats de réalisation
              </Button>
              <button className="text-slate-400 hover:text-slate-600"><MoreVertical className="h-4 w-4" /></button>
              <button className="text-slate-400 hover:text-violet-500"><Mail className="h-4 w-4" /></button>
              <button className="text-slate-400 hover:text-violet-500"><GraduationCap className="h-4 w-4" /></button>
            </div>
          </div>

          {/* Attestations d'assiduité */}
          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5">
            <p className="text-xs text-slate-400 italic mb-3">Jamais généré</p>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                <FileText className="h-5 w-5 text-violet-500" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-900 dark:text-white">Attestations d'assiduité</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" className="text-xs gap-1.5 flex-1" data-testid="btn-zip-attestations">
                <FolderDown className="h-3.5 w-3.5" />Zip des attestations
              </Button>
              <button className="text-slate-400 hover:text-slate-600"><MoreVertical className="h-4 w-4" /></button>
              <button className="text-slate-400 hover:text-violet-500"><Mail className="h-4 w-4" /></button>
              <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer">
                <input type="checkbox" className="rounded border-slate-300" />
                Marquer comme envoyées
              </label>
            </div>
          </div>
        </div>

        {/* Archive section */}
        <button className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-violet-600 transition-colors"
          onClick={() => setArchiveExpanded(!archiveExpanded)} data-testid="btn-toggle-archive">
          Archivage des documents apprenants
          {archiveExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        {archiveExpanded && (
          <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-6" data-testid="archive-section">
            <p className="text-sm text-slate-500 mb-3">Archivez les documents générés pour les apprenants.</p>
            <Button variant="outline" className="text-xs gap-1.5"><Download className="h-3.5 w-3.5" />Télécharger l'archive</Button>
          </div>
        )}
      </div>

      {/* Learner list section */}
      <div className="space-y-4">
        <h3 className="text-base font-bold text-slate-900 dark:text-white">Liste des apprenants</h3>

        {/* View toggle */}
        <div className="flex gap-2" data-testid="view-toggle">
          <Button variant={viewMode === 'grid' ? 'default' : 'outline'}
            className={`text-xs gap-1.5 ${viewMode === 'grid' ? 'bg-violet-600 hover:bg-violet-700 text-white' : ''}`}
            onClick={() => setViewMode('grid')} data-testid="btn-trombinoscope">
            <Grid3X3 className="h-3.5 w-3.5" />Trombinoscope
          </Button>
          <Button variant={viewMode === 'list' ? 'default' : 'outline'}
            className={`text-xs gap-1.5 ${viewMode === 'list' ? 'bg-violet-600 hover:bg-violet-700 text-white' : ''}`}
            onClick={() => setViewMode('list')} data-testid="btn-liste-detaillee">
            <List className="h-3.5 w-3.5" />Liste détaillée des apprenants
          </Button>
        </div>

        {/* Grid view (Trombinoscope) */}
        {viewMode === 'grid' && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="learners-grid">
            {(data?.apprenants || apprenants).map((appr, i) => {
              const name = appr.name || '';
              return (
                <div key={i} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 flex flex-col items-center text-center hover:border-violet-300 transition-colors cursor-pointer"
                  data-testid={`learner-card-${i}`}>
                  <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mb-3">
                    <GraduationCap className="h-8 w-8 text-blue-500" />
                  </div>
                  <p className="text-sm font-medium text-slate-900 dark:text-white">{name}</p>
                </div>
              );
            })}
          </div>
        )}

        {/* List view */}
        {viewMode === 'list' && (
          <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden" data-testid="learners-list">
            {loading ? (
              <div className="px-6 py-8 text-center text-sm text-slate-400">Chargement...</div>
            ) : learners.length === 0 ? (
              <div className="px-6 py-8 text-center text-sm text-slate-400">
                <Users className="h-10 w-10 mx-auto text-slate-300 mb-2" />
                <p>Aucun apprenant dans cette session.</p>
              </div>
            ) : learners.map((appr, i) => {
              const nameParts = (appr.name || '').split(' ');
              const isExpanded = expandedLearner === i;
              return (
                <div key={i} className="border-b border-slate-100 dark:border-slate-700/50" data-testid={`learner-row-${i}`}>
                  <div className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 cursor-pointer"
                    onClick={() => setExpandedLearner(isExpanded ? null : i)}>
                    <div className="w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center shrink-0">
                      <GraduationCap className="h-5 w-5 text-violet-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white">{appr.name}</p>
                      <p className="text-xs text-slate-400">{appr.email || ''}</p>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="flex items-center gap-1.5">
                        <Award className="h-4 w-4 text-amber-400" />
                        <span className="text-xs text-slate-500">0/0</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Star className="h-4 w-4 text-amber-400" />
                        <span className="text-xs text-slate-500">0/0</span>
                      </div>
                      <div className="text-right min-w-[140px]">
                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <div className="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                              <div className="h-full bg-violet-500 rounded-full" style={{ width: `${Math.min((appr.hours_completed / Math.max(appr.total_hours, 1)) * 100, 100)}%` }} />
                            </div>
                          </div>
                          <span className="text-xs font-medium text-slate-600 dark:text-slate-300 whitespace-nowrap">
                            {appr.hours_completed || 0}h / {appr.total_hours || 0}h
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1">{appr.score || 0} / 100</p>
                      </div>
                      {isExpanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="px-5 pb-4 bg-slate-50/50 dark:bg-slate-800/20">
                      <div className="grid grid-cols-3 gap-4 p-4 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                        <div>
                          <p className="text-xs text-slate-400 mb-1">Statut</p>
                          <Badge variant="outline" className={`text-xs ${appr.status === 'actif' ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                            {appr.status === 'actif' ? 'Actif' : 'Inactif'}
                          </Badge>
                        </div>
                        <div>
                          <p className="text-xs text-slate-400 mb-1">Entreprise</p>
                          <p className="text-sm text-slate-700 dark:text-slate-300">{appr.company || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-400 mb-1">Certificats</p>
                          <p className="text-sm text-slate-700 dark:text-slate-300">{appr.certificates_generated ? 'Généré' : 'Non généré'}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SuiviApprenants;
